import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

// 호버 말풍선 한 벌. 브라우저 기본 title은 길이·줄바꿈·표시 시점을 못 정해 긴 안내문이
// 잘려 보였다(2026-10-01 대교, 송출 중 잠금 사유). body로 포탈해 fixed로 띄우니 조상의
// overflow에도 안 잘린다.
//
// 두 길:
//  · useHoverTip — 켜진 요소(ⓘ 등)에 onMouseEnter/onFocus를 직접 단다.
//  · HoverTipLayer — 앱 루트에 한 번. `aria-description`이 있는 요소 위에 마우스가 오면 띄운다.
//    비활성 버튼은 Chrome이 마우스 이벤트를 통째로 삼켜(문서까지도 안 온다) 직접 달 수가 없다.
//    그래서 비활성+aria-description 요소는 pointer-events:none(globals.css)으로 hit-test에서 빼고,
//    문서 mousemove에서 그 자리의 부모를 잡아 좌표로 안의 요소를 찾는다.
const HALF = 150; // max-w 300의 절반

type TipPos = { text: string; top: number; left: number };

function positionOf(el: Element, text: string): TipPos {
  const b = el.getBoundingClientRect();
  const cx = Math.min(Math.max(b.left + b.width / 2, HALF + 8), window.innerWidth - HALF - 8);
  return { text, top: b.bottom + 8, left: cx };
}

function TipBubble({ text, top, left }: TipPos): JSX.Element {
  return createPortal(
    <span
      role="tooltip"
      style={{ position: 'fixed', top, left, transform: 'translateX(-50%)', zIndex: 100000 }}
      // 한국어는 단어 단위로 꺾는다(keep-all) — 글자 단위로 꺾이면 '안/내'처럼 읽기 어렵다. 공백 없는 긴 파일명만 어디서든 꺾는다.
      className="pointer-events-none w-max max-w-[300px] whitespace-pre-line [word-break:keep-all] [overflow-wrap:anywhere] rounded-md bg-[#2b2b2b] px-3 py-2 text-[13px] font-normal normal-case leading-snug tracking-normal text-white shadow-lg"
    >
      <span
        aria-hidden="true"
        style={{ position: 'absolute', top: -5, left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: '5px solid #2b2b2b' }}
      />
      {text}
    </span>,
    document.body,
  );
}

export function useHoverTip(text: string | undefined): {
  props: { onMouseEnter: (e: { currentTarget: Element }) => void; onMouseLeave: () => void; onFocus: (e: { currentTarget: Element }) => void; onBlur: () => void };
  tip: ReactNode;
} {
  const [pos, setPos] = useState<TipPos | null>(null);
  const show = (e: { currentTarget: Element }): void => { if (text) setPos(positionOf(e.currentTarget, text)); };
  const hide = (): void => setPos(null);
  return {
    props: { onMouseEnter: show, onMouseLeave: hide, onFocus: show, onBlur: hide },
    tip: pos && text ? <TipBubble {...pos} /> : null,
  };
}

/** 아무 요소나 감싸 말풍선을 붙인다(켜진 요소용). 감싸는 span은 inline-flex — 줄 안의 아이콘·글자용. */
export function HoverTip({ text, className, children }: { text: string; className?: string | undefined; children: ReactNode }): JSX.Element {
  const { props, tip } = useHoverTip(text);
  return (
    <span className={className ?? 'inline-flex'} {...props}>
      {children}
      {tip}
    </span>
  );
}

const SEL = '[aria-description]';

function tipTargetAt(x: number, y: number): Element | null {
  const under = document.elementFromPoint(x, y);
  if (!under) return null;
  const own = under.closest(SEL);
  if (own) return own;
  // 비활성 요소는 hit-test를 비켜 가므로(pointer-events:none) 부모가 잡힌다 — 안에서 좌표로 찾는다.
  for (const el of under.querySelectorAll(`${SEL}:disabled`)) {
    const b = el.getBoundingClientRect();
    if (x >= b.left && x <= b.right && y >= b.top && y <= b.bottom) return el;
  }
  return null;
}

/** 앱 루트에 한 번. `aria-description`이 있는 요소(비활성 버튼 포함) 위에서 말풍선을 띄운다. */
export function HoverTipLayer(): JSX.Element | null {
  const [pos, setPos] = useState<TipPos | null>(null);
  const current = useRef<Element | null>(null);
  useEffect(() => {
    const place = (el: Element | null): void => {
      if (el === current.current) return;
      current.current = el;
      const text = el?.getAttribute('aria-description');
      setPos(el && text ? positionOf(el, text) : null);
    };
    const onMove = (e: MouseEvent): void => place(tipTargetAt(e.clientX, e.clientY));
    const clear = (): void => place(null);
    const onFocusIn = (e: FocusEvent): void => {
      const el = e.target instanceof Element ? e.target.closest(SEL) : null;
      if (el) place(el);
    };
    document.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseleave', clear);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', clear);
    document.addEventListener('scroll', clear, true);
    document.addEventListener('mousedown', clear, true);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', clear);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', clear);
      document.removeEventListener('scroll', clear, true);
      document.removeEventListener('mousedown', clear, true);
    };
  }, []);
  return pos ? <TipBubble {...pos} /> : null;
}
